/**
 * 消缺 316：操作日志（审计日志）的人话化。
 *
 * 客户看到的是 `member.student.wallet_adjust` / `Student` / 一坨 JSON，
 * 前台根本读不懂，等于这张表只有开发能用。这里把三样东西集中成映射：
 * 动作码 → 中文、对象类型 → 中文、meta 字段 → 中文，
 * 并提供 `formatAuditMeta()` 把 meta 拼成一句话。
 *
 * 放在 shared-types 而不是 admin 里，是因为**导出 CSV 是服务端拼的**——
 * 映射只放前端会让页面上是中文、导出还是原始码，又是两套口径。
 */
/** 动作码 → 中文。key 是写库的原始 action 字符串，全量对齐 apps/api 里的 audit.log 调用。 */
export const AUDIT_ACTION_LABEL = {
    // —— 账号 / 权限 ——
    'account.create': '新建账号',
    'account.promote': '账号提权',
    'account.disable': '停用账号',
    'account.enable': '启用账号',
    'account.preferences.update': '修改偏好设置',
    'auth.c.bind-phone': '家长绑定手机号',
    'member.account.update_self': '修改本人资料',
    'admin.account.edit': '编辑账号',
    'admin.account.void': '作废账号',
    'rbac.account.create': '新建后台账号',
    'rbac.account.assign': '分配角色',
    'rbac.account.update': '修改后台账号',
    'rbac.account.disable': '停用后台账号',
    'rbac.account.enable': '启用后台账号',
    'rbac.account.revoke': '回收后台账号',
    'rbac.role.create': '新建角色',
    'rbac.role.update': '修改角色权限',
    'rbac.role.delete': '删除角色',
    'store_manager.grant': '任命店长',
    'store_manager.update_stores': '调整店长管辖门店',
    'store_manager.revoke': '免去店长',
    'staff.create': '新建员工',
    'system.staff.invite': '邀请员工',
    'system.staff.reset_password': '重置员工密码',
    'system.staff.set_password': '设置员工密码',
    'system.staff.disable': '停用员工',
    'admin.business_staff.create': '新建业务人员',
    'admin.business_staff.edit': '编辑业务人员',
    'admin.business_staff.attach_role': '给已有账号加业务角色',
    'admin.business_staff.disable': '停用业务人员',
    'admin.business_staff.enable': '启用业务人员',
    // —— 学员 / 会员 ——
    'student.create': '家长新增学员',
    'c.student.update': '家长修改学员资料',
    'admin.student.create': '后台建学员档案',
    'admin.student.edit': '编辑学员档案',
    'admin.student.void': '作废学员档案',
    'member.student.wallet_adjust': '调整训练课时',
    'member.student.balance_adjust': '调整钱包余额',
    'member.student.transfer_store': '学员转店',
    'ops.points.adjust': '调整积分',
    'ops.rehab_credit.adjust': '调整康复课时',
    // —— 训练：模板 / 排课 / 预约 ——
    'course.template.create': '新建课程模板',
    'course.template.update': '修改课程模板',
    'course.template.delete': '删除课程模板',
    'course.template.duplicate': '复制课程模板',
    'course.session.create': '新建排课',
    'course_session.create': '教练新建排课',
    'course.session.update': '修改排课',
    'course.session.cancel': '取消排课',
    'course.session.bulk_copy': '批量复制排课',
    'course.session.bulk_delete_day': '按天批量删除排课',
    'b.manager.session.create': '店长新建排课',
    'b.session.cancel_by_coach': '教练取消排课',
    'b.session.reschedule_by_coach': '教练调整排课时间',
    'booking.create': '家长预约课程',
    'booking.cancel': '家长取消预约',
    'booking.complete': '完成课程',
    'b.booking.check_in': '教练签到',
    'b.manager.booking.create': '店长代约课程',
    'admin.booking.assist_create': '后台代约课程',
    'admin.booking.attendance': '修改出勤状态',
    'admin.booking.edit': '编辑预约',
    'admin.booking.void': '作废预约',
    'course.booking.force_cancel': '强制取消预约',
    'course.booking.force_add': '强制加人',
    'admin.training_record.upsert': '后台录训练记录',
    'b.training_record.write': '教练写训练记录',
    'b.training_record.update': '教练改训练记录',
    // —— 康复 ——
    'rehab.package.create': '新建康复套餐',
    'rehab.package.update': '修改康复套餐',
    'rehab.create': '家长预约康复',
    'rehab.complete': '完成康复',
    'rehab.appointment.cancel': '取消康复预约',
    'rehab.appointment.reschedule': '调整康复预约时间',
    'rehab.therapist.update': '修改康复师资料',
    'admin.rehab_appointment.create_slot': '新建康复排班',
    'admin.rehab_appointment.assist_create': '后台代约康复',
    'admin.rehab_appointment.bulk_copy': '批量复制康复排班',
    'admin.rehab_appointment.bulk_delete_day': '按天批量删除康复排班',
    'admin.rehab_appointment.edit': '编辑康复预约',
    'admin.rehab_appointment.void': '作废康复预约',
    'b.rehab_appointment.create_slot': '康复师新建排班',
    'b.rehab_appointment.cancel': '康复师取消预约',
    'b.rehab_appointment.reschedule': '康复师调整预约时间',
    'b.manager.rehab_slot.create': '店长新建康复排班',
    'b.rehab_record.write': '康复师写康复记录',
    'b.rehab_record.update': '康复师改康复记录',
    // —— 调课申请 ——
    'reschedule.request': '提交调课申请',
    'reschedule.approve': '通过调课申请',
    'reschedule.reject': '驳回调课申请',
    // —— 教练 / 排班占用 ——
    'course.coach.update': '修改教练资料',
    'b.busy_block.create': '新建忙碌时段',
    'b.busy_block.delete': '删除忙碌时段',
    // —— 订单 / 交易 ——
    'order.create': '下单',
    'order.cancel': '取消订单',
    'ops.order.cancel': '后台取消订单',
    'ops.order.refund': '订单退款',
    'admin.order.edit': '编辑订单',
    'admin.order.void': '作废订单',
    'admin.order.offline_create': '线下录单',
    'admin.order.zero_confirm': '零元单审核',
    'course.package.create': '新建训练套餐',
    'course.package.update': '修改训练套餐',
    'course.package.delete': '删除训练套餐',
    'ops.recharge_tier.create': '新建充值档位',
    'ops.recharge_tier.update': '修改充值档位',
    'ops.recharge_tier.delete': '删除充值档位',
    // —— 营销：券 / 积分 / 奖品 ——
    'ops.coupon.create': '新建优惠券',
    'ops.coupon.update': '修改优惠券',
    'coupon.issue': '发券',
    'points.exchange.redeem': '积分兑换',
    'ops.exchange.fulfill': '核销兑换',
    'ops.exchange.cancel': '取消兑换',
    'admin.exchange.edit': '编辑兑换记录',
    'admin.exchange.void': '作废兑换记录',
    'ops.reward_item.create': '新建奖品',
    'ops.reward_item.update': '修改奖品',
    // —— 运营内容 ——
    'ops.banner.create': '新建轮播图',
    'ops.banner.update': '修改轮播图',
    'ops.banner.delete': '删除轮播图',
    'ops.banner.reorder': '调整轮播图顺序',
    'ops.tag.create': '新建标签',
    'ops.tag.update': '修改标签',
    'message.broadcast': '群发消息',
    'system.about_us.update': '修改关于我们',
    'system.agreement.update': '修改协议',
    // —— 门店 ——
    'store.create': '新建门店',
    'store.update': '修改门店',
    'store.delete': '删除门店',
    'store.restore': '恢复门店',
    'store.close': '关闭门店',
};
/** 对象类型 → 中文。key 已归一化（小写、去掉下划线），查表前先过 `normalizeAuditType()`。 */
export const AUDIT_TARGET_TYPE_LABEL = {
    account: '账号',
    student: '学员',
    booking: '训练预约',
    coursesession: '排课',
    coursetemplate: '课程模板',
    packagetemplate: '训练套餐',
    trainingrecord: '训练记录',
    rehabappointment: '康复预约',
    rehabpackage: '康复套餐',
    rehabrecord: '康复记录',
    order: '订单',
    coupon: '优惠券',
    usercoupon: '用户券',
    rewarditem: '奖品',
    rewardexchange: '兑换记录',
    exchange: '兑换记录',
    reschedulerequest: '调课申请',
    roledef: '角色',
    store: '门店',
    tag: '标签',
    banner: '轮播图',
    rechargetier: '充值档位',
    busyblock: '忙碌时段',
    systemconfig: '系统配置',
    agreement: '协议',
    message: '消息',
    adminadjust: '后台调账',
};
/**
 * 归一化类型名：同一种东西库里既有 `Student` 也有 `student`、
 * 既有 `RehabAppointment` 也有 `rehab_appointment`（见 record-edit.ts / void.ts）。
 * 比较和查表统一走这个函数。
 */
export function normalizeAuditType(t) {
    return (t ?? '').toLowerCase().replace(/[^a-z]/g, '');
}
/** 归一化后的类型名 → 库里的标准写法（PascalCase），写审计日志时统一用它。 */
export const AUDIT_TARGET_TYPE_CANONICAL = {
    account: 'Account',
    student: 'Student',
    booking: 'Booking',
    coursesession: 'CourseSession',
    coursetemplate: 'CourseTemplate',
    packagetemplate: 'PackageTemplate',
    trainingrecord: 'TrainingRecord',
    rehabappointment: 'RehabAppointment',
    rehabpackage: 'RehabPackage',
    rehabrecord: 'RehabRecord',
    order: 'Order',
    coupon: 'Coupon',
    usercoupon: 'UserCoupon',
    rewarditem: 'RewardItem',
    rewardexchange: 'RewardExchange',
    exchange: 'RewardExchange',
    reschedulerequest: 'RescheduleRequest',
    roledef: 'RoleDef',
    store: 'Store',
    tag: 'Tag',
    banner: 'Banner',
    rechargetier: 'RechargeTier',
    busyblock: 'BusyBlock',
    systemconfig: 'SystemConfig',
    agreement: 'Agreement',
    message: 'Message',
};
/** meta 字段名 → 中文。未收录的字段原样显示字段名，不吞信息。 */
export const AUDIT_META_KEY_LABEL = {
    reason: '原因',
    note: '备注',
    delta: '变动课时',
    deltaCents: '变动金额',
    prevBalance: '变动前',
    nextBalance: '变动后',
    prevCents: '变动前金额',
    nextCents: '变动后金额',
    prevStatus: '原状态',
    nextStatus: '新状态',
    prevAttendance: '原出勤',
    nextAttendance: '新出勤',
    prevWalletCredits: '退款前课时',
    nextWalletCredits: '退款后课时',
    prevRoles: '原角色',
    existingRoles: '原有角色',
    from: '原值',
    to: '新值',
    changed: '改动字段',
    fields: '改动字段',
    keys: '改动字段',
    diff: '改动',
    patch: '改动内容',
    affectedCount: '影响条数',
    affectedBookings: '影响预约数',
    deleted: '删除条数',
    skipped: '跳过条数',
    created: '新增条数',
    count: '条数',
    sent: '发送条数',
    refundedCredit: '退回课时',
    refundCredits: '退回课时',
    refundedCredits: '退回课时',
    creditsRefunded: '退回课时',
    consumedCredit: '扣除课时',
    amountCents: '金额',
    bonusCents: '赠送金额',
    bonusCredits: '赠送课时',
    priceCents: '价格',
    costPoints: '消耗积分',
    credits: '课时',
    sessionId: '排课',
    studentId: '学员',
    bookingId: '预约',
    templateId: '课程模板',
    templateIds: '课程模板',
    storeId: '门店',
    storeIds: '门店',
    fromStoreId: '原门店',
    toStoreId: '新门店',
    homeStoreId: '主门店',
    dealStoreId: '成交门店',
    accountId: '账号',
    parentAccountId: '家长账号',
    couponId: '优惠券',
    refId: '关联对象',
    rewardItemId: '奖品',
    therapistId: '康复师',
    therapistIds: '康复师',
    therapistAccountId: '康复师',
    coachAccountId: '教练',
    assistantCoachId: '助教',
    consultantId: '顾问',
    consultantId2: '顾问 2',
    candidateIds: '候选项',
    ids: '涉及对象',
    roleDefIds: '角色',
    phone: '手机号',
    name: '名称',
    title: '标题',
    type: '类型',
    kind: '种类',
    mode: '方式',
    role: '角色',
    scope: '适用范围',
    source: '来源',
    sourceTagName: '来源标签',
    softDelete: '软删除',
    idempotent: '重复提交',
    keyField: '含关键字段',
    keyFields: '关键字段',
    length: '内容长度',
    version: '版本',
    key: '配置项',
    startAt: '开始时间',
    endAt: '结束时间',
    date: '日期',
    sourceDate: '源日期',
    targetDates: '目标日期',
    sourceWeekStart: '源周起始',
    targetWeekStart: '目标周起始',
    decision: '处理结果',
    adminBypass: '管理员越权',
    breachCapacity: '超出容量',
    zeroConfirm: '零元确认',
    accountCreated: '新建了账号',
    studentCreated: '新建了学员',
    sessionCreated: '新建了排课',
    slotCreated: '新建了排班',
    hadInviteCode: '带邀请码',
    hasStudent: '已绑学员',
    releasedCoupons: '释放优惠券',
    refundId: '退款单号',
    refundAmount: '退款金额',
    refundOrderId: '退款订单',
    payMethod: '支付方式',
    payMethodTagId: '支付方式标签',
    newId: '新对象',
    sourceId: '源对象',
    totalRefund: '退回课时合计',
    pointsTxId: '积分流水',
    walletTxId: '课时流水',
    currency: '币种',
    reversed: '已冲正',
    shortfall: '冲正不足',
    appointmentId: '康复预约',
    // record-edit 可改的业务字段（changed[].field 用的是这些英文名）
    birthday: '生日',
    birthdayPending: '生日待补',
    gender: '性别',
    level: '等级',
    archiveType: '档案类型',
    trainingUnitPriceCents: '训练课时单价',
    rehabUnitPriceCents: '康复课时单价',
    sourceTagId: '来源标签',
    businessTagId: '业务标签',
    businessNote: '业务备注',
    attendanceStatus: '出勤状态',
    attendKind: '正常/体验',
    status: '状态',
    address: '地址',
    avatarUrl: '头像',
    password: '密码',
};
/**
 * 枚举值 → 中文。客户看到的 `gender「female」→「male」`、`方式 regular`
 * 这类英文枚举同样属于「看不懂的机器话」。
 * 只收业务枚举，不收 id——id 走 names 富化。
 */
export const AUDIT_VALUE_LABEL = {
    // 性别
    male: '男',
    female: '女',
    other: '其他',
    // 档案 / 出勤 / 预约状态
    member: '正式会员',
    trial: '体验',
    normal: '正常',
    pending: '待到场',
    checked_in: '已签到',
    no_show: '未到',
    confirmed: '已确认',
    canceled: '已取消',
    completed: '已完成',
    open: '开放',
    full: '已满',
    closed: '已关闭',
    waitlisted: '候补中',
    redeemed: '已兑换',
    fulfilled: '已核销',
    paid: '已支付',
    unpaid: '未支付',
    refunded: '已退款',
    active: '启用',
    disabled: '停用',
    // 预约来源 / 方式
    self: '家长自助',
    manager_assist: '后台代约',
    waitlist_promoted: '候补转正',
    regular: '常规',
    // 角色
    admin: '管理员',
    manager: '店长',
    coach: '教练',
    rehab_th: '康复师',
    user: '家长',
    // 流水原因
    purchase: '购买',
    consume: '消耗',
    refund: '退还',
    recharge: '充值',
    bonus: '赠送',
    admin_adjust: '人工调账',
    // 调课决定
    approve: '通过',
    reject: '驳回',
    reschedule: '改期',
    cancel: '取消',
};
/** meta 里值本身是 id 的字段（渲染时要换成名称）。 */
export const AUDIT_META_ID_KEYS = {
    sessionId: 'CourseSession',
    bookingId: 'Booking',
    studentId: 'Student',
    templateId: 'CourseTemplate',
    templateIds: 'CourseTemplate',
    storeId: 'Store',
    storeIds: 'Store',
    fromStoreId: 'Store',
    toStoreId: 'Store',
    homeStoreId: 'Store',
    dealStoreId: 'Store',
    accountId: 'Account',
    parentAccountId: 'Account',
    couponId: 'Coupon',
    rewardItemId: 'RewardItem',
    therapistId: 'Account',
    therapistIds: 'Account',
    therapistAccountId: 'Account',
    coachAccountId: 'Account',
    assistantCoachId: 'Account',
    consultantId: 'Account',
    consultantId2: 'Account',
    appointmentId: 'RehabAppointment',
    roleDefIds: 'RoleDef',
};
/** 金额类字段（分 → 元）。 */
const CENTS_KEYS = new Set([
    'deltaCents',
    'prevCents',
    'nextCents',
    'amountCents',
    'bonusCents',
    'priceCents',
    'refundAmount',
]);
/** 布尔字段显示成「是 / 否」而不是 true / false。 */
function fmtBool(v) {
    return v ? '是' : '否';
}
function fmtCents(v) {
    return `¥${(v / 100).toFixed(2)}`;
}
/** 形如 cuid / acc_xxx 的 id，用来判断「这串东西是不是该换成名字」。 */
export const ID_LIKE = /^(c[a-z0-9]{20,}|acc_[A-Za-z0-9_]+|stu_[A-Za-z0-9_]+)$/;
/**
 * 只对开发有意义的内部字段，不进展示。
 * 留着会让每条「编辑」日志后面拖一句「含关键字段 否；关键字段 无」，
 * 客户读到的信噪比反而比原始 JSON 还差。原始 meta 仍在 tooltip 里可查。
 */
const INTERNAL_META_KEYS = new Set([
    'keyField',
    'keyFields',
    'idempotent',
    'softDelete',
    'pointsTxId',
    'walletTxId',
    'refundId',
    'candidateIds',
    'ids',
]);
function fmtValue(key, v, opt) {
    if (v === null || v === undefined || v === '')
        return '空';
    if (typeof v === 'boolean')
        return fmtBool(v);
    if (typeof v === 'number')
        return CENTS_KEYS.has(key) ? fmtCents(v) : String(v);
    if (typeof v === 'string') {
        if (ID_LIKE.test(v))
            return opt.names?.[v] ?? v.slice(-8);
        return AUDIT_VALUE_LABEL[v] ?? v;
    }
    if (Array.isArray(v)) {
        if (v.length === 0)
            return '无';
        return v.map((x) => fmtValue(key, x, opt)).join('、');
    }
    if (typeof v === 'object') {
        const o = v;
        // record-edit 的 changed 是 [{field, from, to}]
        if ('field' in o) {
            return `${AUDIT_META_KEY_LABEL[String(o.field)] ?? String(o.field)}「${fmtValue('from', o.from, opt)}」→「${fmtValue('to', o.to, opt)}」`;
        }
        return Object.entries(o)
            .map(([k, val]) => `${AUDIT_META_KEY_LABEL[k] ?? k} ${fmtValue(k, val, opt)}`)
            .join('，');
    }
    return String(v);
}
/**
 * 把 meta 拼成一句人能读的话，例如
 * 「原因 朋友转介绍2节；变动课时 2；变动前 45；变动后 47」。
 *
 * 刻意不做「每个动作一套模板」——130 个动作码写 130 个模板，
 * 加一个接口忘一个模板就又退回原样。按字段翻译 + 通用兜底，永远不会漏信息。
 */
export function formatAuditMeta(meta, opt = {}) {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta))
        return '';
    const entries = Object.entries(meta).filter(([k, v]) => v !== null && v !== undefined && v !== '' && !INTERNAL_META_KEYS.has(k));
    if (entries.length === 0)
        return '';
    return entries
        .map(([k, v]) => `${AUDIT_META_KEY_LABEL[k] ?? k} ${fmtValue(k, v, opt)}`)
        .join('；');
}
/** 动作码 → 中文，查不到就退回原始码（新接口忘了登记时至少不是空白）。 */
export function auditActionLabel(action) {
    return AUDIT_ACTION_LABEL[action] ?? action;
}
/** 对象类型 → 中文，查不到退回原值。 */
export function auditTargetTypeLabel(t) {
    if (!t)
        return '';
    return AUDIT_TARGET_TYPE_LABEL[normalizeAuditType(t)] ?? t;
}
/** 下拉选项：按中文名给出全部动作码。 */
export const AUDIT_ACTION_OPTIONS = Object.entries(AUDIT_ACTION_LABEL).map(([value, label]) => ({ value, label }));
/** 下拉选项：对象类型。value 用库里的标准写法，服务端按归一化匹配。 */
export const AUDIT_TARGET_TYPE_OPTIONS = Object.entries(AUDIT_TARGET_TYPE_CANONICAL)
    .filter(([norm]) => norm !== 'exchange') // exchange 与 rewardexchange 同义，只留一个
    .map(([norm, value]) => ({ value, label: AUDIT_TARGET_TYPE_LABEL[norm] ?? value }));
//# sourceMappingURL=audit-labels.js.map